/**
 * Listener Action Types
 * 
 * Defines all listener action types organized by scope.
 */

// ========================================
// GLOBAL LISTENERS (Login -> Logout)
// ========================================

export const GLOBAL_ACTIONS = {
  ADD_COLLABORATOR: 'ADD_COLLABORATOR',
  REMOVE_COLLABORATOR: 'REMOVE_COLLABORATOR',
  DELETE_SPACE: 'DELETE_SPACE',
  EDIT_SPACE: 'EDIT_SPACE',
  LEAVE_SPACE: 'LEAVE_SPACE',
  // Sent directly to the evicted/leaving user — triggers local unsubscribe + navigation.
  SPACE_ACCESS_REVOKED: 'SPACE_ACCESS_REVOKED',
  // Sent to the user after read-all or clear-all so other tabs can sync state.
  NOTIFICATION_STATE_CHANGED: 'NOTIFICATION_STATE_CHANGED',
  AGENT_TOOL_DISCOVERY_PROGRESS: 'AGENT_TOOL_DISCOVERY_PROGRESS',
  // Phase G5 — Desktop Bridge device presence (pushed via SSE from photon-ws).
  DEVICE_CONNECTED: 'DEVICE_CONNECTED',
  DEVICE_DISCONNECTED: 'DEVICE_DISCONNECTED',
};

// ========================================
// SPECTRA LISTENERS (Enter Spectra -> Leave Spectra)
// ========================================
// Note: Values prefixed with SPECTRA_ to avoid collision with CHAT_METADATA_ACTIONS

export const SPECTRA_ACTIONS = {
  ADD_CHAT: 'ADD_CHAT',
  DELETE_CHAT: 'DELETE_CHAT',
  EDIT_CHAT: 'EDIT_CHAT',
  MOVE_CHAT: 'MOVE_CHAT',
  PIN_UNPIN_CHAT: 'PIN_UNPIN_CHAT',
  DELETE_SPARK: 'DELETE_SPARK',
};

// Handler keys for spectra actions (unique to avoid Map collision)
export const SPECTRA_HANDLER_KEYS = {
  ADD_CHAT: 'SPECTRA_ADD_CHAT',
  DELETE_CHAT: 'SPECTRA_DELETE_CHAT',
  EDIT_CHAT: 'SPECTRA_EDIT_CHAT',
  MOVE_CHAT: 'SPECTRA_MOVE_CHAT',
  PIN_UNPIN_CHAT: 'SPECTRA_PIN_UNPIN_CHAT',
  DELETE_SPARK: 'SPECTRA_DELETE_SPARK',
};

// ========================================
// CHAT METADATA LISTENERS (Login -> Logout)
// ========================================
// Handles standalone spark metadata events (pin, edit, delete)

export const CHAT_METADATA_ACTIONS = {
  PIN_UNPIN_CHAT: 'PIN_UNPIN_CHAT',
  EDIT_CHAT: 'EDIT_CHAT',
  DELETE_CHAT: 'DELETE_CHAT',
};

// Handler keys for chat metadata actions (unique to avoid Map collision)
export const CHAT_METADATA_HANDLER_KEYS = {
  PIN_UNPIN_CHAT: 'METADATA_PIN_UNPIN_CHAT',
  EDIT_CHAT: 'METADATA_EDIT_CHAT',
  DELETE_CHAT: 'METADATA_DELETE_CHAT',
};

// ========================================
// CHAT LISTENERS (Open Chat -> Close Chat)
// ========================================

export const CHAT_ACTIONS = {
  ADD_MESSAGE: 'ADD_MESSAGE',
  EDIT_MESSAGE: 'EDIT_MESSAGE',
  DELETE_MESSAGE: 'DELETE_MESSAGE',
  REGENERATE_MESSAGE: 'REGENERATE_MESSAGE',
  APPROVAL_CREATED: 'APPROVAL_CREATED',
  APPROVAL_UPDATED: 'APPROVAL_UPDATED',
  SSE_RECONNECTED: 'SSE_RECONNECTED', // Synthetic — fired by SSEAdapter on reconnect
  GENERATION_STARTED: 'GENERATION_STARTED',
  GENERATION_COMPLETE: 'GENERATION_COMPLETE',
  GENERATION_STOPPED: 'GENERATION_STOPPED',
  GENERATION_FAILED: 'GENERATION_FAILED',
  ARTIFACT_UPDATED: 'ARTIFACT_UPDATED',
  ARTIFACT_EDIT_STARTED: 'ARTIFACT_EDIT_STARTED',
};

// ========================================
// LISTENER CATEGORIES
// ========================================

export const LISTENER_CATEGORY = {
  GLOBAL: 'global',
  CHAT_METADATA: 'chat_metadata',
  SPECTRA: 'spectra',
  CHAT: 'chat',
};

// ========================================
// HELPER FUNCTIONS
// ========================================

export const getAllActions = () => ({
  ...GLOBAL_ACTIONS,
  ...CHAT_METADATA_ACTIONS,
  ...SPECTRA_ACTIONS,
  ...CHAT_ACTIONS,
});

export const getGlobalActionsList = () => Object.values(GLOBAL_ACTIONS);
export const getChatMetadataActionsList = () => Object.values(CHAT_METADATA_ACTIONS);
export const getSpectraActionsList = () => Object.values(SPECTRA_ACTIONS);
export const getChatActionsList = () => Object.values(CHAT_ACTIONS);
